import { Loader2, Search, X } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { Input } from "src/components/ui/Input";
import { searchCompanies } from "src/lib/portalNotificationService";
import type {
  BroadcastEnv,
  CompanySearchResult,
} from "src/types/portalNotifications";

interface Props {
  env: BroadcastEnv;
  selected: CompanySearchResult[];
  onChange: (next: CompanySearchResult[]) => void;
  disabled?: boolean;
}

const DEBOUNCE_MS = 300;

/**
 * Debounced company search + chip multi-select for SPECIFIC_COMPANIES
 * targeting. Uses the operator-only companies search endpoint — the
 * operator token is attached by `iv3Request`.
 */
export const CompanyTargetPicker: React.FC<Props> = ({
  env,
  selected,
  onChange,
  disabled,
}) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<CompanySearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handle = window.setTimeout(async () => {
      setSearching(true);
      setSearchError(null);
      try {
        setResults(await searchCompanies(env, query));
      } catch (e) {
        setSearchError(
          e instanceof Error ? e.message : "Company search failed",
        );
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [query, env]);

  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const selectedIds = new Set(selected.map((c) => c.id));
  const visibleResults = results.filter((c) => !selectedIds.has(c.id));

  const add = (company: CompanySearchResult) => {
    onChange([...selected, company]);
  };

  const remove = (id: string) => {
    onChange(selected.filter((c) => c.id !== id));
  };

  return (
    <div ref={rootRef} className="relative">
      {selected.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-1.5">
          {selected.map((c) => (
            <span
              key={c.id}
              className="inline-flex items-center gap-1 rounded-full border-2 border-teal-200 bg-teal-50 px-2.5 py-1 text-xs font-bold text-teal-800"
            >
              {c.name}
              <button
                type="button"
                aria-label={`Remove ${c.name}`}
                disabled={disabled}
                onClick={() => remove(c.id)}
                className="rounded-full p-0.5 hover:bg-teal-100"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#9CA3AF]" />
        <Input
          accent="teal"
          value={query}
          disabled={disabled}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder="Search portals by name…"
          className={query || searching ? "pl-9 pr-10" : "pl-9"}
        />
        <div className="absolute right-2 top-1/2 flex -translate-y-1/2 items-center gap-0.5">
          {searching && (
            <Loader2 className="h-4 w-4 animate-spin text-[#9CA3AF]" />
          )}
          {query.length > 0 && !disabled && (
            <button
              type="button"
              aria-label="Clear search"
              onClick={() => {
                setQuery("");
                setOpen(false);
              }}
              className="rounded-full p-1 text-[#9CA3AF] hover:bg-[#F3F4F6] hover:text-[#6B7280]"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        {/* Open upward — section sits near the bottom of the card with no room below. */}
        {open && !disabled && (
          <div className="absolute bottom-full left-0 right-0 z-50 mb-1 max-h-56 overflow-auto rounded-xl border border-[#E5E5E5] bg-white shadow-lg">
            {searchError && (
              <p className="px-3 py-2 text-xs font-semibold text-rose-600">
                {searchError}
              </p>
            )}
            {!searchError && visibleResults.length === 0 && !searching && (
              <p className="px-3 py-2 text-xs text-[#9CA3AF]">
                No matching portals
              </p>
            )}
            {visibleResults.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => add(c)}
                className="flex w-full items-center justify-between px-3 py-2 text-left text-sm font-semibold text-[#1F1F1F] hover:bg-[#E8F5E4]"
              >
                <span className="truncate">{c.name}</span>
                {c.shortcode && (
                  <span className="ml-2 shrink-0 text-xs font-bold text-[#9CA3AF]">
                    {c.shortcode}
                  </span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CompanyTargetPicker;
